/**
 * Figma Node IDs:
 * Design Frame: 1103:66034
 * Instance IDs: I1103:66034;1686:150880, I1103:66034;1686:150244, I1103:66034;1686:150564, I1103:66034;1701:153087;1701:155051, I1103:66034;1103:60579, I1103:66034;1701:153087
 * Composite: true
 * Last Updated: 2025-01-15
 * Generated by: Claude Code figma-component command
 */
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl, defineMessages } from "react-intl";

import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { LxButton } from "@labxchange/ui-components/lib/LxButton/index";
import Snackbar, {
  SnackbarVariant,
  SnackbarProvider,
} from "@labxchange/ui-components/lib/Snackbar";
import { ChevronLeft, ChevronRight } from "@labxchange/icons";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";

import * as styles from "./MicroscopeSorter.css";
import emptyImage from "./assets/empty.png";
import {
  initialize,
  submitAnswer,
  reset,
  SortedItem,
  microscopeSorterReducer,
} from "./MicroscopeSorterReduxSlice";
import { fetchTranslations } from "./locales/config";
import { SimManager } from "@learningblocks/simulation-engine";

export { microscopeSorterReducer, reset as resetMicroscopeSorter };
export type { SortedItem } from "./MicroscopeSorterReduxSlice";

const messages = defineMessages({
  ascusLabel: {
    id: "MicroscopeSorter.ascusLabel",
    defaultMessage: "Ascus:",
    description: "Label for the ascus counter",
  },
  previousAscus: {
    id: "MicroscopeSorter.previousAscus",
    defaultMessage: "Previous ascus",
    description: "Aria label for the previous ascus navigation button",
  },
  nextAscus: {
    id: "MicroscopeSorter.nextAscus",
    defaultMessage: "Next ascus",
    description: "Aria label for the next ascus navigation button",
  },
  emptySlide: {
    id: "MicroscopeSorter.emptySlide",
    defaultMessage: "Empty microscope slide",
    description: "Alt text for empty microscope slide image",
  },
  ascusOfTotal: {
    id: "MicroscopeSorter.ascusOfTotal",
    defaultMessage: "Ascus {current} of {total}",
    description: "Alt text showing current ascus number out of total",
  },
  submitAnswer: {
    id: "MicroscopeSorter.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Button text to submit the current answer",
  },
  submitAndGoToLab: {
    id: "MicroscopeSorter.submitAndGoToLab",
    defaultMessage: "Submit answers and go back to the lab",
    description: "Button text to submit all answers and proceed to lab",
  },
  addSlide: {
    id: "MicroscopeSorter.addSlide",
    defaultMessage: "Add a slide to the microscope.",
    description: "Snackbar message prompting user to add a slide",
  },
});

export interface MicroscopeSorterOption {
  value: string;
  label: string;
}

export interface MicroscopeSorterProps {
  // Required props - core behavior
  images: string[];
  totalCount: number;
  questionText: string;
  finalSubmissionText: string;
  options: MicroscopeSorterOption[];

  // Optional props - can be controlled externally or managed internally
  currentIndex?: number;
  selectedOption?: string | null;
  onNavigateLeft?: () => void;
  onNavigateRight?: () => void;
  onSelectOption?: (value: string) => void;
  onSubmit?: () => void;
  onCountsChange?: (parentalCount: number, recombinantCount: number) => void;

  // Props with defaults
  submitDisabled?: boolean;
  isEmpty?: boolean;
  randomizeImages?: boolean;
}

const MicroscopeSorterInternal: React.FC<MicroscopeSorterProps> = ({
  images,
  totalCount,
  questionText,
  finalSubmissionText,
  options,
  currentIndex: controlledCurrentIndex,
  selectedOption: controlledSelectedOption,
  onNavigateLeft: externalOnNavigateLeft,
  onNavigateRight: externalOnNavigateRight,
  onSelectOption: externalOnSelectOption,
  onSubmit: externalOnSubmit,
  onCountsChange,
  submitDisabled,
  isEmpty = false,
  randomizeImages = false,
}) => {
  const intl = useIntl();
  let dispatch: any;
  let hasRedux = false;
  let sortedItems: any[] = [];
  let reduxRandomizedImages: string[] = [];
  let reduxCurrentIndex = 0;

  try {
    dispatch = useDispatch();
    const microscopeSorterState = useSelector(
      (state: any) => state.microscopeSorter
    );
    sortedItems = microscopeSorterState?.sortedItems || [];
    reduxRandomizedImages = microscopeSorterState?.randomizedImages || [];
    reduxCurrentIndex = microscopeSorterState?.currentIndex ?? 0;
    hasRedux = true;
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    dispatch = () => {};
    hasRedux = false;
  }

  const [internalCurrentIndex, setInternalCurrentIndex] = React.useState(0);
  const [internalSelectedOption, setInternalSelectedOption] = React.useState<
    string | null
  >(null);
  const [internalRandomizedImages, setInternalRandomizedImages] =
    React.useState<string[]>([]);
  const [showSnackbar, setShowSnackbar] = React.useState(isEmpty);
  const [internalIsLastQuestionSubmitted, setInternalIsLastQuestionSubmitted] =
    React.useState(false);
  const [internalSortedItems, setInternalSortedItems] = React.useState<
    SortedItem[]
  >([]);
  const [hasInitializedFromRedux, setHasInitializedFromRedux] =
    React.useState(false);
  const [isImageLoading, setIsImageLoading] = React.useState(false);

  // Use Redux state if available, otherwise local state
  const randomizedImages =
    hasRedux && reduxRandomizedImages.length > 0
      ? reduxRandomizedImages
      : internalRandomizedImages;

  const isLastQuestionSubmitted = internalIsLastQuestionSubmitted;

  const isControlled =
    controlledCurrentIndex !== undefined ||
    controlledSelectedOption !== undefined;
  // Use controlled or internal state for currentIndex
  const currentIndex = isControlled
    ? (controlledCurrentIndex ?? 0)
    : internalCurrentIndex;
  const selectedOption = isControlled
    ? (controlledSelectedOption ?? null)
    : internalSelectedOption;
  const isSubmitDisabled =
    submitDisabled ?? (!selectedOption && !isLastQuestionSubmitted);

  // Initialize Redux state on mount
  React.useEffect(() => {
    if (hasRedux) {
      dispatch(
        initialize({
          totalCount,
          images,
          randomize: randomizeImages ?? false,
        })
      );
    }
  }, [dispatch, totalCount, images, randomizeImages, hasRedux]);

  // Restore internal state from Redux on mount (for persistence)
  // This only runs once on mount to restore persisted state
  React.useEffect(() => {
    if (hasRedux && !hasInitializedFromRedux) {
      // Only restore if there's persisted data
      if (sortedItems.length > 0) {
        setInternalSortedItems(sortedItems);

        // Restore current index from Redux only if it's valid
        if (reduxCurrentIndex !== undefined && reduxCurrentIndex !== null) {
          setInternalCurrentIndex(reduxCurrentIndex);
          // Restore selected option for current index
          const currentItem = sortedItems.find(
            (item: SortedItem) => item.index === reduxCurrentIndex
          );
          if (currentItem) {
            setInternalSelectedOption(currentItem.selectedOption);
          }
        }
      }
      // Mark as initialized regardless, to prevent future syncs
      setHasInitializedFromRedux(true);
    }
  }, [hasRedux, hasInitializedFromRedux]);

  // Local randomization fallback when Redux is not available
  React.useEffect(() => {
    if (!hasRedux) {
      if (randomizeImages && images.length > 0) {
        const shuffled = [...images].sort(() => Math.random() - 0.5);
        setInternalRandomizedImages(shuffled);
      } else {
        setInternalRandomizedImages(images);
      }
    }
  }, [images, randomizeImages, hasRedux]);

  React.useEffect(() => {
    setShowSnackbar(isEmpty);
  }, [isEmpty]);

  React.useEffect(() => {
    if (onCountsChange) {
      const parentalCount = internalSortedItems.filter(
        (item) => item.selectedOption === "parental"
      ).length;
      const recombinantCount = internalSortedItems.filter(
        (item) => item.selectedOption === "recombinant"
      ).length;
      onCountsChange(parentalCount, recombinantCount);
    }
  }, [internalSortedItems, onCountsChange]);

  const handleNavigateLeft = () => {
    // If in final submission view, go back to editing mode
    if (showFinalSubmission) {
      if (!isControlled) {
        setInternalIsLastQuestionSubmitted(false);
        // Navigate to last question to allow editing
        const lastIndex = images.length - 1;
        setInternalCurrentIndex(lastIndex);
        const lastItem = internalSortedItems.find(
          (item) => item.index === lastIndex
        );
        setInternalSelectedOption(lastItem?.selectedOption || null);
      }
      return;
    }

    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      if (!isControlled) {
        setInternalCurrentIndex(newIndex);
        // Load the selection for the previous index
        const previousItem = internalSortedItems.find(
          (item) => item.index === newIndex
        );
        setInternalSelectedOption(previousItem?.selectedOption || null);
      }
      externalOnNavigateLeft?.();
    }
  };

  const handleNavigateRight = () => {
    // Check if current question has been answered before navigating
    const currentHasAnswer = internalSortedItems.some(
      (item) => item.index === currentIndex
    );

    if (currentHasAnswer && currentIndex < images.length - 1) {
      const newIndex = currentIndex + 1;
      if (!isControlled) {
        setInternalCurrentIndex(newIndex);
        // Load the selection for the next index
        const nextItem = internalSortedItems.find(
          (item) => item.index === newIndex
        );
        setInternalSelectedOption(nextItem?.selectedOption || null);
      }
      externalOnNavigateRight?.();
    }
  };

  const handleSelectOption = (value: string) => {
    if (!isEmpty) {
      if (!isControlled) {
        // Only update UI selection state, don't persist to internal state yet
        setInternalSelectedOption(value);
      }
      externalOnSelectOption?.(value);
    }
  };

  const handleSubmit = () => {
    // FINAL SUBMIT: User clicked "Submit answers and go back to the lab"
    if (showFinalSubmission) {
      // Data is already persisted to Redux, just call external callback
      externalOnSubmit?.();
      return;
    }

    // INDIVIDUAL SUBMIT: User clicked "Submit answer" to navigate forward
    if (!isSubmitDisabled && !isEmpty && selectedOption) {
      // Persist the selection to both internal state and Redux only when submitting
      if (!isControlled) {
        setInternalSortedItems((prev) => {
          const existingIndex = prev.findIndex(
            (item) => item.index === currentIndex
          );
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              selectedOption: selectedOption,
            };
            return updated;
          } else {
            return [
              ...prev,
              { index: currentIndex, selectedOption: selectedOption },
            ];
          }
        });
      }

      if (hasRedux) {
        dispatch(submitAnswer({ index: currentIndex, option: selectedOption }));
      }

      if (!isControlled) {
        // Navigate to next question or show final submission
        if (currentIndex < images.length - 1) {
          setIsImageLoading(true);
          const newIndex = currentIndex + 1;
          setInternalCurrentIndex(newIndex);
          // Load the selection for the next index
          const nextItem = internalSortedItems.find(
            (item) => item.index === newIndex
          );
          setInternalSelectedOption(nextItem?.selectedOption || null);
        } else {
          // Last question - show final submit button
          setInternalIsLastQuestionSubmitted(true);
          SimManager.getInstance().setInputValue("slide_control", {
            slideRecorded: true,
          });
          SimManager.getInstance().updateState(
            "meiosis_notebook",
            "calculator"
          );
          SimManager.getInstance().updateState(
            "meiosis_calculator",
            "calculator"
          );
        }
      }
    }
  };

  const displayImages = isEmpty ? [emptyImage] : randomizedImages;
  const currentImage = displayImages[currentIndex] || "";

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  // Check if current question has been answered
  const currentHasAnswer = internalSortedItems.some(
    (item) => item.index === currentIndex
  );

  // Show final submission UI only when user explicitly completes the last question
  const showFinalSubmission = isLastQuestionSubmitted;

  // Navigation logic
  const isLeftNavDisabled =
    isEmpty || (currentIndex === 0 && !showFinalSubmission);
  const isRightNavDisabled =
    isEmpty || currentIndex >= displayImages.length - 1 || !currentHasAnswer;

  return (
    <SnackbarProvider>
      <div
        className={styles.microscopeSorterContainer}
        data-testid="microscope-sorter"
      >
        {/* Header Section */}
        <div className={styles.header}>
          {/* Placeholder for layout balance */}
          <div className={styles.placeholder} />

          <div className={styles.counter}>
            <span className={styles.counterLabel}>
              <RichText value={intl.formatMessage(messages.ascusLabel)} />
            </span>
            <span className={styles.counterValue}>
              <span>{currentIndex + 1}</span>
              <span>/{totalCount}</span>
            </span>
          </div>

          {/* Placeholder for layout balance */}
          <div className={styles.placeholder} />
        </div>

        {/* Microscope View Section */}
        <div className={styles.microscopeSection}>
          <LxButton
            variant="ghost"
            size="large"
            shape="circle"
            Icon={ChevronLeft}
            onClickHandler={handleNavigateLeft}
            disabled={isLeftNavDisabled}
            ariaLabel={intl.formatMessage(messages.previousAscus)}
            testId="navigate-left-button"
            className={styles.navButton}
          />

          <div className={styles.microscopeViewer}>
            {isImageLoading && (
              <div className={styles.loadingOverlay}>
                <div className={styles.spinner} />
              </div>
            )}
            <img
              src={currentImage}
              alt={
                isEmpty
                  ? intl.formatMessage(messages.emptySlide)
                  : intl.formatMessage(messages.ascusOfTotal, {
                      current: currentIndex + 1,
                      total: totalCount,
                    })
              }
              className={styles.microscopeImage}
              onLoad={handleImageLoad}
            />
          </div>

          <LxButton
            variant="ghost"
            size="large"
            shape="circle"
            Icon={ChevronRight}
            onClickHandler={handleNavigateRight}
            disabled={isRightNavDisabled}
            ariaLabel={intl.formatMessage(messages.nextAscus)}
            testId="navigate-right-button"
            className={styles.navButton}
          />
        </div>

        {/* Question Section */}
        <div className={styles.questionCard}>
          <div
            className={
              showFinalSubmission
                ? styles.questionTextCentered
                : styles.questionText
            }
          >
            <RichText
              value={showFinalSubmission ? finalSubmissionText : questionText}
            />
          </div>

          {!showFinalSubmission && (
            <div className={styles.optionsContainer}>
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.optionButton} ${
                    selectedOption === option.value
                      ? styles.optionButtonSelected
                      : ""
                  }`}
                  onClick={() => handleSelectOption(option.value)}
                  aria-pressed={selectedOption === option.value}
                  data-testid={`option-${option.value}`}
                  disabled={isEmpty}
                >
                  <RichText value={option.label} />
                </button>
              ))}
            </div>
          )}

          <div
            className={
              showFinalSubmission
                ? styles.submitContainerCentered
                : styles.submitContainer
            }
          >
            <LxButton
              variant="primary"
              size="medium"
              text={
                showFinalSubmission
                  ? intl.formatMessage(messages.submitAndGoToLab)
                  : intl.formatMessage(messages.submitAnswer)
              }
              onClickHandler={handleSubmit}
              disabled={isEmpty || isSubmitDisabled}
              ariaLabel={
                showFinalSubmission
                  ? intl.formatMessage(messages.submitAndGoToLab)
                  : intl.formatMessage(messages.submitAnswer)
              }
              testId="submit-button"
            />
          </div>
        </div>

        {/* Snackbar for empty state */}
        {showSnackbar && (
          <Snackbar
            variant={SnackbarVariant.GUIDANCE}
            message={intl.formatMessage(messages.addSlide)}
            open={showSnackbar}
            onOpenChange={setShowSnackbar}
          />
        )}
      </div>
    </SnackbarProvider>
  );
};

export const MicroscopeSorter = (props: MicroscopeSorterProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <MicroscopeSorterInternal {...props} />
  </TranslationsProvider>
);
